import { useEffect, useState } from "react";

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  language_code?: string;
}

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  close: () => void;
  initData: string;
  initDataUnsafe: {
    user?: TelegramUser;
    query_id?: string;
  };
  colorScheme: "light" | "dark";
  themeParams: {
    bg_color?: string;
    text_color?: string;
    hint_color?: string;
    link_color?: string;
    button_color?: string;
    button_text_color?: string;
  };
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    setText: (text: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
  };
  HapticFeedback: {
    impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  /**
   * Telegram WebApp external link opener.
   * iOS/Android clientlariga qarab qo'shimcha options qo'llab-quvvatlanadi.
   */
  openLink: (
    url: string,
    options?: {
      try_instant_view?: boolean;
      try_browser?: boolean;
    }
  ) => void;
  showPopup: (params: {
    title?: string;
    message: string;
    buttons?: Array<{ type: string; text?: string; id?: string }>;
  }) => void;
  platform: string;
  version: string;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

export function useTelegram() {
  const [tg, setTg] = useState<TelegramWebApp | null>(null);
  const [user, setUser] = useState<TelegramUser | null>(null);
  // Sinxron lazy initializer: birinchi render paytida o'qiladi,
  // useEffect dan oldin — Telegram Desktop da initData allaqachon mavjud bo'lsa
  // darhol olinadi, race condition bo'lmaydi
  const [initDataRaw, setInitDataRaw] = useState<string>(
    () => window.Telegram?.WebApp?.initData ?? ""
  );
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    if (webApp) {
      webApp.ready();
      webApp.expand();
      setTg(webApp);
      setUser(webApp.initDataUnsafe?.user ?? null);
      // useEffect ichida ham yangilaymiz — lazy init paytida bo'sh bo'lgan holat uchun
      const data = webApp.initData ?? "";
      setInitDataRaw(data);
      setIsReady(true);
    } else {
      setIsReady(true);
    }
  }, []);

  const haptic = (type: "light" | "medium" | "heavy" = "light") => {
    tg?.HapticFeedback?.impactOccurred(type);
  };

  const successHaptic = () => {
    tg?.HapticFeedback?.notificationOccurred("success");
  };

  return { tg, user, initDataRaw, isReady, haptic, successHaptic };
}
